/**
 * FileDiffModal Component
 * 
 * Modal to view file differences using Monaco DiffEditor.
 * Shows side-by-side or inline diff between previous and current versions.
 * Similar to GitHub's diff view.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DiffEditor } from '@monaco-editor/react';
import { FileDiffData } from '../../../../hooks/useReleases';

interface FileDiffModalProps {
  /** Diff data with both file versions */
  diffData: FileDiffData;
  /** Whether diff is loading */
  isLoading: boolean;
  /** Close modal callback */
  onClose: () => void;
}

/**
 * Get language from filename extension
 */
function getLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'js': case 'mjs': return 'javascript';
    case 'ts': case 'tsx': return 'typescript';
    case 'jsx': return 'javascript';
    case 'json': return 'json';
    case 'html': case 'htm': return 'html';
    case 'css': return 'css';
    case 'scss': case 'sass': return 'scss';
    case 'md': case 'markdown': return 'markdown';
    case 'xml': return 'xml';
    case 'yaml': case 'yml': return 'yaml';
    case 'sh': case 'bash': return 'shell';
    case 'sql': return 'sql';
    default: return 'plaintext';
  }
}

/**
 * Get badge styles for change type
 */
function getChangeBadge(changeType: FileDiffData['changeType']): { bg: string; text: string; label: string } {
  switch (changeType) {
    case 'added':
      return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Added' };
    case 'modified':
      return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Modified' };
    case 'deleted':
      return { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deleted' };
  }
}

export default function FileDiffModal({
  diffData,
  isLoading,
  onClose,
}: FileDiffModalProps) {
  const [viewMode, setViewMode] = useState<'side-by-side' | 'inline'>('side-by-side');
  const [isFullscreen, setIsFullscreen] = useState(true); // Default to fullscreen for better diff view
  
  const language = getLanguage(diffData.filename);
  const badge = getChangeBadge(diffData.changeType);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // For added files, show only the new content
  const isAddedFile = diffData.changeType === 'added';
  const isDeletedFile = diffData.changeType === 'deleted';

  // Dynamic editor height based on fullscreen mode
  const editorHeight = isFullscreen ? 'calc(100vh - 140px)' : '60vh';

  // Use portal to render modal outside of component hierarchy
  // This ensures it covers the entire screen, not just the parent component
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      style={{ padding: isFullscreen ? 0 : '1rem' }}
      onClick={handleBackdropClick}
    >
      <div 
        className={`
          bg-[#1e1e1e] overflow-hidden shadow-2xl border border-white/10 flex flex-col
          ${isFullscreen 
            ? 'w-full h-full' 
            : 'w-full max-w-6xl max-h-[90vh] rounded-xl'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#3c3c3c]">
          <div className="flex items-center gap-3 min-w-0">
            {/* File Icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white/50 flex-shrink-0">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2"/>
              <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2"/>
            </svg>
            
            {/* Filename */}
            <span className="text-sm font-medium text-white truncate">{diffData.filename}</span>
            
            {/* Change Badge */}
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0 ${badge.bg} ${badge.text}`}>
              {badge.label}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle (only for modified files) */}
            {!isAddedFile && !isDeletedFile && (
              <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/10">
                <button
                  onClick={() => setViewMode('side-by-side')}
                  className={`
                    px-2.5 py-1 rounded-md text-xs font-medium transition-all
                    ${viewMode === 'side-by-side' 
                      ? 'bg-white/10 text-white' 
                      : 'text-white/50 hover:text-white/70'}
                  `}
                >
                  Side by Side
                </button>
                <button
                  onClick={() => setViewMode('inline')}
                  className={`
                    px-2.5 py-1 rounded-md text-xs font-medium transition-all
                    ${viewMode === 'inline' 
                      ? 'bg-white/10 text-white' 
                      : 'text-white/50 hover:text-white/70'}
                  `}
                >
                  Inline
                </button>
              </div>
            )}

            {/* Fullscreen Toggle Button */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors flex-shrink-0"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                // Exit fullscreen icon (minimize)
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                </svg>
              ) : (
                // Fullscreen icon (maximize)
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                </svg>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors flex-shrink-0"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Diff Labels */}
        {!isAddedFile && !isDeletedFile && viewMode === 'side-by-side' && (
          <div className="flex border-b border-[#3c3c3c]">
            <div className="flex-1 px-4 py-1.5 bg-red-500/5 border-r border-[#3c3c3c]">
              <span className="text-xs text-red-400 font-medium">Previous Version</span>
            </div>
            <div className="flex-1 px-4 py-1.5 bg-emerald-500/5">
              <span className="text-xs text-emerald-400 font-medium">Current Version</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3 text-white/50">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Loading diff...</span>
              </div>
            </div>
          ) : isAddedFile ? (
            // For added files, show single editor with green highlight indicator
            <div className="h-full flex flex-col">
              <div className="px-4 py-2 bg-emerald-500/10 border-b border-emerald-500/20">
                <span className="text-xs text-emerald-400">New file added</span>
              </div>
              <DiffEditor
                height={editorHeight}
                language={language}
                original=""
                modified={diffData.currentContent}
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: isFullscreen },
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  renderSideBySide: false,
                  scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                  wordWrap: 'on',
                }}
              />
            </div>
          ) : isDeletedFile ? (
            // For deleted files, show the previous content
            <div className="h-full flex flex-col">
              <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20">
                <span className="text-xs text-red-400">File deleted</span>
              </div>
              <DiffEditor
                height={editorHeight}
                language={language}
                original={diffData.previousContent || ''}
                modified=""
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: isFullscreen },
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  renderSideBySide: false,
                  scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                  wordWrap: 'on',
                }}
              />
            </div>
          ) : (
            // For modified files, show full diff
            <DiffEditor
              height={editorHeight}
              language={language}
              original={diffData.previousContent || ''}
              modified={diffData.currentContent}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: isFullscreen },
                fontSize: 13,
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                renderSideBySide: viewMode === 'side-by-side',
                scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                wordWrap: 'on',
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-t border-[#3c3c3c]">
          <div className="flex items-center gap-4 text-xs text-white/40">
            {!isAddedFile && !isDeletedFile && (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-red-500/30"></span>
                  Removed
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-emerald-500/30"></span>
                  Added
                </span>
              </>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="
              px-3 py-1.5 rounded-md text-sm font-medium
              text-white bg-[#208AEC] hover:bg-[#208AEC]/80
              transition-colors
            "
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

